import React, { useCallback, useEffect, useState } from "react";
import MyEditor from "../../../components/MyEditor";
import WebLayout from "../../../layout/WebLayout";
import UploadImage from "../../../components/UploadImage";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import ProductSpecificationEditor from "../../../components/ProductSpecificationEditor";
import ProductAction from "../../../stores/actions/ProductAction";
import { RootState } from "../../../stores";
import CategoryAction from "../../../stores/actions/categoryAction";
import { toast } from "react-toastify";
import { Link, useNavigate, useParams } from "react-router-dom";
import HSNAction from "../../../stores/actions/HSNAction";
import Select from "react-select";

type FormData = {
  id: number;
  product_name: string;
  sku: string;
  barcode: string;
  selling_qty: number;
  description: string;
  ex_factory_price: any;
  tax: number;
  vendor_id: number;
  category_id: number;
  sub_category_id: number;
  tags: string;
  is_variant: any;
  product_specification: string;
  product_image: string;
  product_code: any;
  min_sale_qty: number;
  mrp: number;
};

function EditProduct() {
  const { hsnList } = useSelector((state: RootState) => state.hsn);
  const { categoryList } = useSelector((state: RootState) => state.category);
  const { subCategoryList } = useSelector((state: RootState) => state.category);

  const [selectedCategory, setSelectedCategory] = useState<any>();
  const [selectedSubcategory, setSelectedSubcategory] = useState<any>();
  const [selectedOption, setSelectedOption] = useState<any>();
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [editorData, setEditorData] = useState<string>("");
  const [specification, setSpecification] = useState<string>("");

  const handleEditorChange = (newData: string) => {
    setEditorData(newData);
  };

  const handleProductSpecificEditorChange = (newData: string) => {
    setSpecification(newData);
  };

  const [images, setImages] = useState<any>([]);
  const dispatch = useDispatch()<any>;
  const navigate = useNavigate();

  const [isVariants, setVariants] = useState<boolean>(false);

  const {
    register,
    handleSubmit,

    setValue,
    formState: {},
  } = useForm<FormData>();

  const getCategoryList = useCallback(async () => {
    await dispatch(
      CategoryAction.getCategory({
        search: "",
        limit: 1000,
        offset: 0,
        keyword: "",
        sortBy: "",
        sortType: "",
      })
    );
  }, [dispatch]);

  const getSubCategoryList = useCallback(async () => {
    await dispatch(
      CategoryAction.getSubCategory({
        search: "",
        limit: 1000,
        offset: 0,
        keyword: "",
        sortBy: "",
        sortType: "",
        category: selectedCategory,
        type: "filter",
      })
    );
  }, [dispatch, selectedCategory]);

  const getChildCategoryList = useCallback(async () => {
    await dispatch(
      CategoryAction.getChildCategory({
        search: "",
        limit: 1000,
        offset: 0,
        keyword: "",
        sortBy: "",
        sortType: "",
        sub_category: selectedSubcategory,
        type: "filter",
      })
    );
  }, [dispatch, selectedSubcategory]);

  const getHSNList = useCallback(async () => {
    await dispatch(HSNAction.getList());
  }, [dispatch, selectedSubcategory]);

  useEffect(() => {
    getHSNList();
    getCategoryList();
    getSubCategoryList();
  }, [
    getHSNList,
    getCategoryList,
    getSubCategoryList,
    selectedCategory,
    selectedSubcategory,
  ]);

  const handleCategoryChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSelectedCategory(Number(event.target.value));
    setSelectedSubcategory(null);
  };

  const handleSubcategoryChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSelectedSubcategory(Number(event.target.value));
  };

  const handleChange = (value: any) => {
    setSelectedOption(value);
  };

  const { id } = useParams<any>();

  const { productDetails } = useSelector((state: RootState) => state.product);

  const getProductDetailsData = useCallback(async () => {
    await dispatch(
      ProductAction.getProductDetails({
        id: Number(id),
      })
    );
  }, [dispatch, id]);

  useEffect(() => {
    getProductDetailsData();
  }, [getProductDetailsData]);

  useEffect(() => {
    setValue(
      "is_variant",
      productDetails && productDetails.is_variant === "YES"
    );
    setValue("product_name", productDetails && productDetails.product_name);
    setValue("product_code", productDetails && productDetails.product_code);
    setValue("sku", productDetails && productDetails.sku);
    setValue("barcode", productDetails && productDetails.barcode);
    setValue("ex_factory_price", productDetails && productDetails.landing_cost);
    setValue("min_sale_qty", productDetails?.min_sale_qty);
    setSelectedCategory(productDetails?.category_id);
    setSelectedSubcategory(productDetails?.sub_category_id);
    setValue("category_id", productDetails && productDetails.category_id);
    setValue(
      "sub_category_id",
      productDetails && productDetails.sub_category_id
    );
    setVariants(productDetails?.is_variant === "YES");

    setSelectedOption({
      label: productDetails?.hsnTaxLabel,
      value: productDetails?.hsnTax,
    });

    setImages(
      productDetails?.images.length > 0
        ? productDetails.images.map((d: any) => [
            {
              path: d.product_image,
              lastModified: 1691469981795,
              name: d.product_image,
              size: 288022,
              type: "image/png",
              webkitRelativePath: "",
              place: "back",
            },
          ])
        : []
    );
    setValue("mrp", productDetails && productDetails.mrp);
  }, [productDetails]);

  const formHandleSubmit = async (data: any, e: any) => {
    e.preventDefault();
    if (selectedOption === null) {
      toast.error("HSN Code is required");
      return false;
    }
    setIsLoader(true);

    try {
      await dispatch(
        ProductAction.updateProduct({
          id: Number(id),
          product_name: data.product_name,
          barcode: data.barcode,
          selling_qty: data.selling_qty,
          description: editorData,
          ex_factory_price: data.ex_factory_price,
          tax: selectedOption.value,
          vendor_id: data.vendor_id,
          category_id: data.category_id,
          sub_category_id: data.sub_category_id,
          is_variant: data.is_variant ? "YES" : "NO",
          product_specification: specification,
          product_image: images,
          product_code: data.product_code,
          min_sale_qty: data.min_sale_qty,
          mrp: data.mrp,
        })
      );
      setIsLoader(false);
      navigate("/product-list");
      toast.success("Product Updated Successfully");
    } catch (error: any) {
      setIsLoader(false);
      toast.error(error.message);
    }
  };

  console.log(isVariants);

  return (
    <WebLayout pageName={"AddProduct"}>
      <div className="content-wrapper">
        <div className="container-fluid flex-grow-1 container-p-y">
          <div className="app-ecommerce">
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-2">
              <h4 className="mb-1">Edit Product</h4>
              <Link to="/product-list" className="btn btn-primary btn-sm">
                <i className="bx bx-chevron-left bx-sm align-middle"></i> Back
              </Link>
            </div>
            <form onSubmit={handleSubmit(formHandleSubmit)}>
              <div className="row">
                <div className="col-12 col-lg-8">
                  <div className="card mb-4">
                    <div className="card-header d-flex justify-content-between">
                      <h5 className="card-tile mb-0">Product information</h5>
                      <label htmlFor="variants">
                        <input
                          type="checkbox"
                          id={"variants"}
                          checked={isVariants}
                          {...register("is_variant")}
                          onChange={() => setVariants(!isVariants)}
                        />{" "}
                        Product Have Variants ?
                      </label>
                    </div>
                    <div className="card-body">
                      <div className="row mb-3">
                        <div className="col">
                          <label className="form-label">
                            Name <small className={"text-danger"}>*</small>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Product Name"
                            {...register("product_name")}
                            required={true}
                          />
                        </div>
                      </div>
                      {!isVariants ? (
                        <div className="row mb-3">
                          <div className="col">
                            <label className="form-label">
                              Product Code{" "}
                              <small className={"text-danger"}>*</small>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Product Code"
                              {...register("product_code")}
                              required={!isVariants}
                            />
                          </div>
                          <div className="col">
                            <label className="form-label">
                              Barcode <small className={"text-danger"}>*</small>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id="ecommerce-product-barcode"
                              placeholder="0123-4567"
                              {...register("barcode")}
                            />
                          </div>
                        </div>
                      ) : null}

                      <div>
                        <label className="form-label">
                          Description <small className={"text-danger"}>*</small>
                        </label>
                        <MyEditor
                          initialValue={
                            productDetails && productDetails.description
                          }
                          onChange={handleEditorChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="card mb-4">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <label className="form-label">
                        Upload Image <small className={"text-danger"}>*</small>
                      </label>
                    </div>
                    <div className="card-body">
                      <UploadImage images={images} setImages={setImages} />
                    </div>
                  </div>

                  <div className="card mb-4">
                    <div className="card-body">
                      <label className="form-label">
                        Product Specification &nbsp;
                        <span className="text-muted">(Optional)</span>
                      </label>
                      <ProductSpecificationEditor
                        initialValue={
                          productDetails && productDetails.product_specification
                        }
                        onChange={handleProductSpecificEditorChange}
                      />
                    </div>
                  </div>
                </div>

                <div className="col-12 col-lg-4">
                  {!isVariants ? (
                    <div className="card mb-4">
                      <div className="card-header">
                        <h5 className="card-title mb-0">Pricing</h5>
                      </div>
                      <div className="card-body">
                        <div className="mb-3">
                          <label className="form-label">
                            MRP <small className={"text-danger"}>*</small>
                          </label>
                          <input
                            type="number"
                            step="any"
                            className="form-control"
                            id="ecommerce-product-discount-price"
                            placeholder="MRP"
                            {...register("mrp")}
                            required={!isVariants}
                          />
                        </div>

                        <div className="mb-3">
                          <label className="form-label">
                            Ex Factory Price{" "}
                            <span className="text-success">(Without Tax)</span>
                            <small className={"text-danger"}>*</small>
                          </label>
                          <input
                            type="number"
                            step="any"
                            className="form-control"
                            id="ecommerce-product-discount-price"
                            placeholder="Ex Factory Price"
                            {...register("ex_factory_price")}
                            required={!isVariants}
                          />
                        </div>

                        <div className="mb-3">
                          <label className="form-label">
                            Case Pack Size Qty{" "}
                            <small className={"text-danger"}>*</small>
                          </label>
                          <input
                            type="number"
                            step="any"
                            className="form-control"
                            id="ecommerce-product-discount-price"
                            placeholder="Case Pack Size Qty"
                            {...register("min_sale_qty")}
                            required={!isVariants}
                          />
                        </div>
                      </div>
                    </div>
                  ) : null}

                  <div className="card mb-4">
                    <div className="card-header">
                      <h5 className="card-title mb-0">HSN and GST</h5>
                    </div>
                    <div className="card-body">
                      <div className="mb-3">
                        <label className="form-label">
                          HSN Code <small className={"text-danger"}>*</small>
                        </label>

                        <Select
                          defaultValue={selectedOption}
                          value={selectedOption}
                          onChange={handleChange}
                          options={hsnList}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="card mb-4">
                    <div className="card-header">
                      <h5 className="card-title mb-0">Categories</h5>
                    </div>
                    <div className="card-body">
                      <div className="mb-3 col ecommerce-select2-dropdown">
                        <label className="form-label mb-1">
                          Category <small className={"text-danger"}>*</small>
                        </label>
                        <select
                          id="vendor"
                          className="select2 form-select"
                          {...register("category_id")}
                          value={selectedCategory || ""}
                          onChange={handleCategoryChange}
                          required={true}>
                          <option value="" disabled>
                            Select Category
                          </option>
                          {categoryList &&
                            categoryList.map((catList: any) => (
                              <option key={catList.id} value={catList.id}>
                                {catList.name}
                              </option>
                            ))}
                          ;
                        </select>
                      </div>

                      <div className="mb-3 col ecommerce-select2-dropdown">
                        <label className="form-label mb-1">Sub-Category</label>
                        <select
                          id="vendor"
                          className="select2 form-select"
                          {...register("sub_category_id")}
                          value={selectedSubcategory || ""}
                          onChange={handleSubcategoryChange}>
                          <option value="" disabled>
                            Select Sub-Category
                          </option>
                          {subCategoryList &&
                            subCategoryList.map((subCatList: any) => (
                              <option key={subCatList.id} value={subCatList.id}>
                                {subCatList.name}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <button
                type="submit"
                className="btn btn-primary px-4"
                disabled={isLoader}>
                {isLoader ? <i className={"fa fa-spinner fa-spin"}></i> : ""}
                &nbsp; Update Product
              </button>
            </form>
          </div>
        </div>
      </div>
    </WebLayout>
  );
}

export default EditProduct;
